import React, { useState } from "react";

interface PasswordInputProps {
  error?: boolean;
  errorText?: string;
  size?: "lg" | "md"; // Explicitly define size as a union type
  placeholder?: string;
  [key: string]: any; // Allow additional props
}

const PasswordInput : React.FC<PasswordInputProps> = ({
  error = false,
  errorText,
  size = "lg",
  placeholder,
  customClassName,
  ...props
}) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  let styleBasedSizeProp = {
    lg: "py-3 px-3 h-12",
    md: "px-2 py-[0.375rem] h-9",
  };

  let styleBasedErrorProp = !error
    ? "border alu-border active:border-[#FF861A] focus:border-[#FF861A]"
    : "border-2 border-[#F33D62] active:border-[#F33D62] focus:border-[#F33D62]";

  const togglePasswordVisibility = () => {
    setIsPasswordVisible((prev) => !prev);
  };

  return (
    <div>
      <div className="relative">
        <input
          type={isPasswordVisible ? "text" : "password"}
          className={`custom-textinput ${styleBasedSizeProp[size]} w-full text-sm outline-0 ${styleBasedErrorProp} focus:hover:bg-[#FFF3E5] hover:border-transparent hover:bg-[#FFF3E5] bg-white active:text-[#343A3D] text-[#333] rounded pr-14 ${customClassName}`}
          placeholder={placeholder}
          {...props}
          onKeyDown={
            props.onKeyDown
              ? props.onKeyDown
              : (e) => {
                if (e.key === "Enter") e.preventDefault();
              }
          }
        />
        <button
          onClick={togglePasswordVisibility}
          type="button"
          className="absolute top-[0.25rem] right-2 bottom-[0.25rem] p-[0.75rem] flex items-center"
        >
          {isPasswordVisible ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 22 20"
              fill="none"
            >
              <path
                d="M15.1 10.6001L13.65 9.1501C13.8 8.36677 13.575 7.63343 12.975 6.9501C12.375 6.26676 11.6 6.0001 10.65 6.1501L9.2 4.7001C9.48333 4.56676 9.77083 4.46676 10.0625 4.4001C10.3542 4.33343 10.6667 4.3001 11 4.3001C12.25 4.3001 13.3125 4.7376 14.1875 5.6126C15.0625 6.4876 15.5 7.5501 15.5 8.8001C15.5 9.13343 15.4667 9.44593 15.4 9.7376C15.3333 10.0293 15.2333 10.3168 15.1 10.6001ZM18.3 13.7501L16.85 12.3501C17.4833 11.8668 18.0458 11.3376 18.5375 10.7626C19.0292 10.1876 19.45 9.53343 19.8 8.8001C18.9667 7.11677 17.7708 5.77926 16.2125 4.7876C14.6542 3.79593 12.9167 3.3001 11 3.3001C10.5167 3.3001 10.0417 3.33343 9.575 3.4001C9.10833 3.46676 8.65 3.56676 8.2 3.7001L6.65 2.1501C7.33333 1.86676 8.03333 1.65426 8.75 1.5126C9.46667 1.37093 10.2167 1.3001 11 1.3001C13.5167 1.3001 15.7583 1.99593 17.725 3.3876C19.6917 4.77926 21.1167 6.58343 22 8.8001C21.6167 9.78343 21.1125 10.6959 20.4875 11.5376C19.8625 12.3793 19.1333 13.1168 18.3 13.7501ZM18.8 19.9001L14.6 15.7501C14.0167 15.9334 13.4292 16.0709 12.8375 16.1626C12.2458 16.2543 11.6333 16.3001 11 16.3001C8.48333 16.3001 6.24167 15.6043 4.275 14.2126C2.30833 12.8209 0.883333 11.0168 0 8.8001C0.35 7.91677 0.791667 7.09593 1.325 6.3376C1.85833 5.57926 2.46667 4.9001 3.15 4.3001L0.4 1.5001L1.8 0.100098L20.2 18.5001L18.8 19.9001ZM4.55 5.7001C4.06667 6.13343 3.625 6.60843 3.225 7.1251C2.825 7.64177 2.48333 8.2001 2.2 8.8001C3.03333 10.4834 4.22917 11.8209 5.7875 12.8126C7.34583 13.8043 9.08333 14.3001 11 14.3001C11.3333 14.3001 11.6583 14.2793 11.975 14.2376C12.2917 14.1959 12.6167 14.1501 12.95 14.1001L12.05 13.1501C11.8667 13.2001 11.6917 13.2376 11.525 13.2626C11.3583 13.2876 11.1833 13.3001 11 13.3001C9.75 13.3001 8.6875 12.8626 7.8125 11.9876C6.9375 11.1126 6.5 10.0501 6.5 8.8001C6.5 8.61677 6.5125 8.44176 6.5375 8.2751C6.5625 8.10843 6.6 7.93343 6.65 7.7501L4.55 5.7001Z"
                fill="#808080"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
            >
              <g clipPath="url(#clip0_8574_246)">
                <path
                  d="M12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17ZM12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9Z"
                  fill="#343A3D"
                />
              </g>
              <defs>
                <clipPath id="clip0_8574_246">
                  <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>
          )}
        </button>
      </div>
      {errorText && (
        <p className="alu-text-error text-sm mt-2">
          {errorText ? `*${errorText}` : "＊エラーテキストが入ります"}
        </p>
      )}
    </div>
  );
};

export default PasswordInput;
